import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DateLostInTimeResult,
  MultipleTimeZoneResult,
  TimeZoneChangeEditor,
  TimeZoneFilters
} from '../../models/time-zones/time-zone.models';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { PtLuxonDate } from '../../models/projects/date.models';

@Injectable()
export class TimeZoneService {
  config = AppConfig.settings;
  private readonly timeZoneTable: DbTable;
  timeZones: TimeZoneChangeEditor[];
  // we need to keep track of when the user last updated their timezones to know if we should refresh them from the local db
  timeZonesUpdatedAt: DateTime;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateParserService: DateParserService,
  ) {
    this.timeZoneTable = this.dbContext.tables.find(t => t.name === 'TimeZoneData');
  }

  // because of how often we need to get all timezones (any time a date is deserialised, or time entered, or duration added to a date),
  // we don't want to continuously keep hitting the local WebSQL database, so we store timezones as a global variable.
  // The problem with this is that if a user deletes or adds a timezone, then this global variable won't necessarily
  // update for other users, so we need to periodically update this global variable from the WebSQL db.
  async getAllTimeZones(): Promise<TimeZoneChangeEditor[]> {
    const needUpdate = DateTime.local().minus({second: 10}) > this.timeZonesUpdatedAt;

    if (needUpdate || !this.timeZones || this.timeZones.length == 0) {
      await this.updateTimeZones();
    }

    return this.timeZones;
  }

  async updateTimeZones() {
    let timeZones = await this.dbContext.getAll(this.timeZoneTable) as TimeZoneChangeEditor[]

    if (!timeZones || timeZones.length === 0) {
      await this.updateTimeZonesFromApi();
      timeZones = await this.dbContext.getAll(this.timeZoneTable) as TimeZoneChangeEditor[]
    }

    timeZones = await this.deserializeTimeZones(timeZones, false);
    this.timeZones = timeZones;
    this.timeZonesUpdatedAt = DateTime.local();
  }

  async deserializeTimeZones(timeZones: TimeZoneChangeEditor[], convertToLocal: boolean): Promise<TimeZoneChangeEditor[]> {
    for (let i = 0; i < timeZones.length; i++) {
      timeZones[i].dateTime = await this.dateParserService.deserializeDate(timeZones[i].dateTime, convertToLocal, this);
      timeZones[i].timeOfChange = await this.dateParserService.deserializeDate(timeZones[i].timeOfChange, convertToLocal, this);
    }

    return timeZones;
  }

  async updateTimeZonesFromApi(): Promise<TimeZoneChangeEditor[]> {
    const url = `${this.config.apiUrl}api/timezone/getall`;
    const result = await this.http
      .get<TimeZoneChangeEditor[]>(url)
      .toPromise();
    await this.saveTimeZoneChangeMultipleEditors(result);

    return result;
  }

  getEditor(filters: TimeZoneFilters): Promise<TimeZoneChangeEditor> {
    const url = `${this.config.apiUrl}api/timezone/editor`;

    filters = this.dateParserService.removeOffsetFromLuxonDates(filters);

    return this.http.post<TimeZoneChangeEditor>(url, filters).toPromise()
      .then(async (editor: TimeZoneChangeEditor) => {
        return await this.dateParserService.deserializeDates(editor, true, this).then(result => {
          return result;
        });
      });
  }

  async getEditorByIdLocally(id: number): Promise<TimeZoneChangeEditor> {
    return await this.dbContext.get(this.timeZoneTable, id) as TimeZoneChangeEditor;
  }

  async getPreviousTimeZone(currentZone: TimeZoneChangeEditor): Promise<TimeZoneChangeEditor> {
    let zones = await this.getAllTimeZones();

    zones = await this.dateParserService.deserializeDates(zones);

    if (zones.length === 1) {
      return zones.find(p => true);
    }

    const sortedZones = zones
      .sort((a, b) => a.timeOfChange.toMillis() - b.timeOfChange.toMillis());
    const currentZoneIndex = sortedZones
      .findIndex(p => p.id === currentZone.id);

    if (currentZoneIndex !== (-1 || 0)) {
      return sortedZones[currentZoneIndex - 1];
    }

    return null;
  }

  // filters.dateAmbiguous == true if date is local or a date just entered by a user (i.e no tz applied yet), if date is UTC it should be false for a correct comparison to be made
  async getEditorLocally(filters: TimeZoneFilters): Promise<TimeZoneChangeEditor> {
    if (!filters.dateTime) {
      return null;
    }

    let zones =  await this.getAllTimeZones();

    // deserialise dates but no convert to local - that will happen in the below loop if datesAmbiguous is set to true
    // zones = await this.dateParserService.deserializeDates(zones, false);

    if (zones.length === 1) {
      return zones.find(p => true);
    }

    // if (filters.dateAmbiguous)
    // can't compare the date in filters to the dates saved on the timezone change - the saved dates are in utc but the entered date isn't
    // so we either need to convert the saved dates to local (apply offset) - or convert the entered date to UTC
    // we can't convert the entered date to UTC without knowing the offset (which we don't know until this method runs) so we have to
    // convert the saved dates to local time to do the comparisons below

    // filters = await this.dateParserService.deserializeDates(filters, this);

    const currentZone = zones
      .filter(p => {
        let tzDate = p.timeOfChange as DateTime;
        const filtersDate = filters.dateTime;

        tzDate = tzDate.setZone('utc', {keepLocalTime: false});

        if (filters.dateAmbiguous) {
          tzDate = tzDate.plus({seconds: p.previousSecondsOffsetFromUtc});
        } else {
          return tzDate.toMillis() <= filtersDate.toMillis();
        }

        return tzDate.toMillis() <= filtersDate.toMillis();
      })
      .sort((a, b) => b.timeOfChange.valueOf() - a.timeOfChange.valueOf())
      .find(p => true);

    // const currentZone = zones
    //   .filter(p => {
    //     let tzDate = p.timeOfChange as DateTime;
    //     const filtersDate = filters.dateTime;
    //     tzDate = tzDate.plus({seconds: p.secondsOffsetFromUtc});
    //
    //     // // tz date will be deserialised from above and be in UTC time
    //     // // if dateAmbiguous, we
    //     // if (filters.dateAmbiguous) {
    //     //   // convert tz date to local time
    //     //   tzDate = tzDate.setZone(PtLuxonDate.getOffsetString(p.secondsOffsetFromUtc), {keepLocalTime: false});
    //     // } else {
    //     //   tzDate = tzDate.plus({seconds: p.secondsOffsetFromUtc});
    //     //   // tzDate = tzDate.setZone(PtLuxonDate.getOffsetString(p.secondsOffsetFromUtc), {keepLocalTime: false});
    //     // }
    //
    //     return tzDate <= filtersDate;
    //   })
    //   .sort((a, b) => b.timeOfChange.valueOf() - a.timeOfChange.valueOf())
    //   .find(p => true);

    return currentZone;
  }

  // returns timezones sorted ascending
  async getTimeZoneChangesBetweenDates(startDate: DateTime, endDate: DateTime): Promise<TimeZoneChangeEditor[]> {
    let timeZones = await this.getAllTimeZones()

    timeZones = await this.dateParserService.deserializeDates(timeZones);

    // todo - check if still need to remove local offsets like this after de-serialising
    // timeZones.forEach(z => {
    //   // remove local offset
    //   z.timeOfChange = new Date(z.timeOfChange.getTime() + z.timeOfChange.getTimezoneOffset() * 60000);
    // });

    return timeZones
      .filter(p => p.timeOfChange > startDate && p.timeOfChange < endDate)
      .sort(function (a, b) {
        // ascending
        // todo - check date comparison
        if (a.dateTime > b.dateTime) {
          return +1;
        }
        // todo - check date comparison is correctly working with datetime
        if (a.dateTime < b.dateTime) {
          return -1;
        }
        return 0;
      });
  }

  // if dateAmbiguous = true, remove the offset
  async checkForNegativeTimeZone(date: DateTime): Promise<MultipleTimeZoneResult> {
    if (!date) {
      return null;
    }

    const result = new MultipleTimeZoneResult();
    const zones = await this.getAllTimeZones();

    result.result = false;
    result.timeZone = null;

    if (zones.length === 1) {
      return result; // No timezone ambiguity if only one zone is present
    }

    result.possibleOffsets = [];

    for (const zone of zones) {
      if (zone.previousSecondsOffsetFromUtc < zone.secondsOffsetFromUtc) {
        continue; // Only account for negative timezone changes
      }

      const lowerBoundary = zone.timeOfChange.plus({ seconds: zone.secondsOffsetFromUtc });
      const upperBoundary = zone.timeOfChange.plus({ seconds: zone.previousSecondsOffsetFromUtc });

      if (date.hasSame(lowerBoundary, 'day') && date >= lowerBoundary && date <= upperBoundary) {
        result.result = true;
        result.timeZone = cloneDeep(zone);
        result.possibleOffsets.push(zone.secondsOffsetFromUtc, zone.previousSecondsOffsetFromUtc);
        break; // Only add offsets for the relevant timezone change
      }
    }

    // Deduplicate offsets
    result.possibleOffsets = Array.from(new Set(result.possibleOffsets));

    return result;
  }

  checkForDateLostInTime(ptLuxonDate: PtLuxonDate, timeZone: TimeZoneChangeEditor): DateLostInTimeResult {
    const previousOffsetDate = timeZone.timeOfChange.plus({seconds: timeZone.previousSecondsOffsetFromUtc});
    const newOffsetDate = timeZone.timeOfChange.plus({seconds: timeZone.secondsOffsetFromUtc});

    if (ptLuxonDate.dateTime >= previousOffsetDate && ptLuxonDate.dateTime < newOffsetDate) {
      const isLostInTime = new DateLostInTimeResult();

      isLostInTime.result = true;
      isLostInTime.timeZone = timeZone;

      return isLostInTime;
    }

    return null;
  }

  async saveEditor(editor: TimeZoneChangeEditor, isDefault: boolean = false): Promise<TimeZoneChangeEditor> {
    if (isDefault) {
      // if isDefault then we are saving the very first tz changes during initial app setup, where we don't want the removal
      // of the local offset to have an effect on the serialised iso time string, so set zone to utc here first
      editor.dateTime = editor.dateTime ? editor.dateTime.setZone('utc', {keepLocalTime: true}) : null;
      editor.timeOfChange = editor.timeOfChange ? editor.timeOfChange.setZone('utc', {keepLocalTime: true}) : null;
    } else {
      editor = this.dateParserService.removeOffsetFromLuxonDates(editor);
    }

    const url = `${this.config.apiUrl}api/timezone/save`;
    const newEditor = await this.http.post<TimeZoneChangeEditor>(url, editor).toPromise();
    await this.dbContext.addOrUpdate(this.timeZoneTable, newEditor.id, newEditor);
    await this.updateTimeZones();

    return newEditor;
  }

  // Does the passed in date coincide with a time zone change thus making it lost in time.
  async isDateLostInTime(dateTime: DateTime, dateIsLocal?: boolean): Promise<DateLostInTimeResult> {
    const result = new DateLostInTimeResult();
    let zones = await this.getAllTimeZones()

    if (!dateTime) {
      result.result = false;
      return result;
    }

    const newDateZone = await this.getEditorLocally({dateTime: dateTime, dateAmbiguous: dateIsLocal});

    if (!newDateZone || zones.length < 2) {
      result.result = false;
      result.timeZone = newDateZone;
      return result;
    }

    if (!dateIsLocal) {
      // date HAS to be local, because there is no way to know which times are lost or not without applying the in-app timezone
      // offsets to the date on the timeZoneEditor, so if it's not local (so it's utc), then we need to apply the current offset to make
      // it local so that the comparison below isn't trying to compare local dates with a utc date
      // dateTime = new Date(date.getTime() + (newDateZone.secondsOffsetFromUtc * 1000));
    }

    for (let i = 0; i < zones.length; i++) {
      const zone = zones[i] as TimeZoneChangeEditor;

      if (zone.dateTime && zone.timeOfChange) {
        // new date = date value + (javascript system offset removed) + (previous in-app timezone applied)
        // let previousOffsetDate = new Date(zone.timeOfChange.getTime()
        //   + (zone.timeOfChange.getTimezoneOffset() * 60000)
        //   + (zone.previousSecondsOffsetFromUtc * 1000));
        const previousOffsetDate = zone.timeOfChange.plus({seconds: zone.previousSecondsOffsetFromUtc});
        // new date = date value + (javascript system offset removed) + (new in-app timezone applied)
        // let newOffsetDate = new Date(zone.timeOfChange.getTime()
        //   + (zone.timeOfChange.getTimezoneOffset() * 60000)
        //   + (zone.secondsOffsetFromUtc * 1000));
        const newOffsetDate = zone.timeOfChange.plus({seconds: zone.secondsOffsetFromUtc});

        if (dateTime >= previousOffsetDate && dateTime < newOffsetDate) {
          result.result = true;
          result.timeZone = zone;
          return result;
        }
      }
    }

    result.result = false;
    result.timeZone = await this.getEditorLocally({dateTime: dateTime, dateAmbiguous: true});
    return result;
  }

  async getLatestTimeZoneChange(): Promise<TimeZoneChangeEditor> {
    const url = `${this.config.apiUrl}api/timezone/latestzone`;

    const result = await this.http
      .get(url)
      .toPromise();

    return await this.dateParserService.deserializeDates(result, true, this);
  }

  // dateAmbiguous == true if date is local or a date just entered by a user (i.e no tz applied yet), if date is UTC it should be false for a correct comparison to be made
  async offsetForTime(dateTime: DateTime, usePreviousOffset?: boolean, dateAmbiguous?: boolean) {
    if (!dateTime) {
      return null;
    }

    const filters = new TimeZoneFilters();
    filters.dateTime = dateTime;
    filters.dateAmbiguous = dateAmbiguous;
    const editor = await this.getEditorLocally(filters);

    if (!editor) {
      return 0;
    }
    if (usePreviousOffset) {
      return Math.round(editor.previousSecondsOffsetFromUtc / 3600);
    } else {
      return Math.round(editor.secondsOffsetFromUtc / 3600);
    }
  }

  // remove a timeZone from local appdata, and then updates the shared timeZone property in this service
  async deleteLocalTimeZone(timeZoneId: number) {
    await this.dbContext.remove(this.timeZoneTable, timeZoneId);
    await this.updateTimeZones();
  }

  async deleteAllTimeZoneChangeEditors() {
    await this.dbContext.removeAll(this.timeZoneTable);
  }

  async saveTimeZoneChangeMultipleEditors(editors: TimeZoneChangeEditor[]) {
    for (let i = 0; i < editors.length; i++) {
      await this.dbContext.addOrUpdate(this.timeZoneTable, editors[i].id, editors[i]);
    }
  }

  async getStartOfDayForDate(date: DateTime): Promise<DateTime> {
    date = this.dateParserService.removeOffsetFromLuxonDate(date);
    const url = `${this.config.apiUrl}api/timezone/getstartofday?date=${date.toJSON()}`;
    const dateString = await this.http.get<string>(url).toPromise();
    return await this.dateParserService.deserializeDate(dateString, true, this);
  }

  async getEndOfDayForDate(date: DateTime): Promise<DateTime> {
    date = this.dateParserService.removeOffsetFromLuxonDate(date);
    const url = `${this.config.apiUrl}api/timezone/getendofday?date=${date.toJSON()}`;
    const dateString = await this.http.get<string>(url).toPromise();
    return await this.dateParserService.deserializeDate(dateString, true, this);
  }
}



// WEBPACK FOOTER //
// ./src/services/time-zone/time-zone.service.ts